import { Collapsible as CollapsibleRoot, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible'
import { ChevronDownIcon } from 'lucide-react'
import React, { useId } from 'react'

type CollapsibleRootProps = React.ComponentProps<typeof CollapsibleRoot>

interface CollapsibleContainerProps {
  children: React.ReactNode;
  /**
   * Use `defaultOpen` to make the collapsed state uncontrolled (i.e. managed
   * internally by this component) while still setting the initial state. To
   * externally control the collapsed state, use the `open` prop instead.
   */
  defaultOpen?: boolean;
  /**
   * Per the accordion ARIA design pattern, the trigger is contained in a
   * heading element. The heading level defaults to `3` but should be adjusted
   * based on the context in which the component is used.
   */
  heading: React.ReactNode;
  /**
   * Defines a slot for an arbitrary icon to be rendered on the right side of
   * the heading. Do not render an interactive elements here, as the entire
   * heading is wrapped in a button element.
   */
  headingIconContent?: React.ReactNode;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  onOpenChange?: CollapsibleRootProps['onOpenChange'];
  /**
   * Use `open` to make the collapsed state fully controlled by the owner component.
   */
  open?: boolean;
}

export function CollapsibleContainer ({
  children,
  defaultOpen = true,
  heading,
  headingIconContent,
  headingLevel = 3,
  onOpenChange,
  open,
}: CollapsibleContainerProps) {
  const headingId = useId()

  const Heading = `h${headingLevel}` as keyof React.JSX.IntrinsicElements

  return (
    <CollapsibleRoot
      className='border border-gray-400 rounded-lg shadow-md group/root has-[.trigger:hover]:shadow-lg transition'
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      open={open}
    >
      <Heading>
        <CollapsibleTrigger asChild>
          <button className='appearance-none trigger flex text-left items-start w-full body text-gray-800 p-4 gap-2 outline outline-2 outline-transparent focus-visible:outline-thatch-500 rounded-lg'>
            <div className='group-data-[state=open]/root:rotate-180 h-5 flex items-center'>
              <ChevronDownIcon aria-hidden size={16} />
            </div>
            <div id={headingId}>{heading}</div>
            {!!headingIconContent && (
              <span className='ml-auto h-5 flex items-center'>
                {headingIconContent}
              </span>
            )}
          </button>
        </CollapsibleTrigger>
      </Heading>
      {/**
       * Since this component may be used in forms, we want the collapsed
       * content to always be in the DOM so that the fields are included in the
       * form regardless of the `open` state. Radix UI's `<CollapsibleContent>`
       * does not mount the content by default when it is closed, so we need
       * to use the `forceMount` prop to keep the content in the DOM.
       *
       * However, Radix UI's `<CollapsibleContent>` component does not have
       * good support for both "force mounting" the content and animating the
       * height via CSS animations. Everything works great when the component
       * is first mounted in an "open" state, but when the component is first
       * mounted in a "closed" state, Radix UI disables the CSS animation so
       * we can't rely on the animation to apply the CSS properties we need
       * to collapse the content.
       *
       * To work around this, we're wrapping the `<CollapsibleContent>` in a
       * container that uses a transition on the `max-height` property to
       * visually hide the content when it is closed, even on the first
       * render, while still allowing the smooth open/close animation when the
       * `open` state is toggled.
       */}
      <div className='max-h-0 overflow-hidden invisible group-data-[state=open]/root:max-h-[1000rem] group-data-[state=open]/root:overflow-visible group-data-[state=open]/root:visible transition-[max-height,overflow,visibility] duration-300'>
        <CollapsibleContent
          aria-labelledby={headingId}
          className='data-[state=open]:animate-collapsible-open data-[state=closed]:animate-collapsible-close'
          data-testid='collapsible-content'
          forceMount
          role='region'
        >
          <div className='border-t border-gray-300'>{children}</div>
        </CollapsibleContent>
      </div>
    </CollapsibleRoot>
  )
}
